// The auth seam (plan.md §2, item 1; §4.4).
//
// Every call to Supabase Auth lives here. Replacing it with a self-hosted
// identity service means rewriting these five functions; nothing else in the
// app imports supabase.auth.
//
// Logging in is entirely optional and mostly cosmetic in v1: reporting and
// corroborating both work fully anonymously. Signing in only adds a user_id
// alongside the anonymous session token, so "my reports" survives clearing
// browser storage or moving to another device.

import { supabase, isConfigured, DbError } from './db.js';

/**
 * Send a magic link. No password is ever created, stored or asked for.
 *
 * `emailRedirectTo` must be an allowed redirect URL in the Supabase project
 * (Authentication → URL Configuration), or the link in the email will bounce
 * to the site root instead of back to where the user was.
 */
export async function sendMagicLink(email) {
  if (!isConfigured) throw new DbError('auth.noDb');

  const { error } = await supabase.auth.signInWithOtp({
    email: email.trim(),
    options: {
      emailRedirectTo: window.location.origin + window.location.pathname,
      shouldCreateUser: true,
    },
  });

  if (error) {
    // Supabase rate-limits magic links per address; say so plainly rather
    // than showing a raw error.
    if (error.status === 429) {
      throw new DbError('auth.rateLimited');
    }
    throw new DbError('auth.sendFail', null, error);
  }
}

/** The current session, or null. Resolves the magic-link return automatically. */
export async function getSession() {
  if (!isConfigured) return null;
  const { data } = await supabase.auth.getSession();
  return data?.session ?? null;
}

export async function getUser() {
  const session = await getSession();
  return session?.user ?? null;
}

export async function signOut() {
  if (!isConfigured) return;
  await supabase.auth.signOut();
}

/**
 * Subscribe to sign-in/sign-out. Returns an unsubscribe function.
 * Fires on the magic-link return too, which is how the UI knows to swap out
 * of the "check your email" state without a reload.
 */
export function onAuthChange(callback) {
  if (!isConfigured) return () => {};
  const { data } = supabase.auth.onAuthStateChange((_event, session) => {
    callback(session?.user ?? null);
  });
  return () => data?.subscription?.unsubscribe();
}

/**
 * Supabase puts auth tokens in the URL fragment on the magic-link return.
 * Once detectSessionInUrl has consumed them, strip them so the address bar
 * stays a clean, shareable link (plan.md §4.3 wants the URL always valid to
 * copy) and so tokens do not sit in browser history.
 */
export function cleanAuthParamsFromUrl() {
  if (!window.location.hash.includes('access_token')
      && !window.location.search.includes('error_description')) return;

  const url = new URL(window.location.href);
  url.hash = '';
  url.searchParams.delete('error');
  url.searchParams.delete('error_code');
  url.searchParams.delete('error_description');
  window.history.replaceState({}, '', url.toString());
}
